<template>
  <ProjectScaffold :loading="loadingDocuments">
    <div class="tabs" v-if="selectedTabId">
      <TabsNavigation :tab-definitions="tabDefinitions" v-model="selectedTabId" />
      <TabsContent :tab-definitions="tabDefinitions" v-model="selectedTabId" class="tabs-content">
        <div v-if="selectedTabId === 'creator'">
          <div class="header">
            <div class="titles">
              <div class="title">Project documents - THE SOURCE x CREATOR</div>
              <div class="subtitle">
                Please upload here all documents related to the projects: Quote, Validated Quote, Purchase Order,
                Invoice, etc
              </div>
            </div>
            <div class="actions flex-row items-end">
              <CreateDocument class="mb-4" :resumable="resumable" @input="handleDocumentAdded" />
              <div class="">
                <FilterPicker :value="currentCreatorFilter" @input="handleCreatorFilterSelectChange" />
              </div>
            </div>
          </div>
          <ts-divider></ts-divider>
          <DocumentList
            label="Project documents"
            :documents="creatorDocuments"
            :can-upload="isProjectManager || isCreator"
            @document-delete="handleDocumentDelete"
            @document-edit="handleDocumentEdit"
            @documents-download="handleDocumentsDownload"
            @file-added="handleFileAdded('creator', $event)"
            @file-success="handleFileSuccess"
            @file-error="handleFileError"
          />
        </div>
        <div v-if="selectedTabId === 'brand'">
          <div class="header">
            <div class="titles">
              <div class="title">Project documents - THE SOURCE x BRAND</div>
              <div class="subtitle">
                Please upload here all documents related to the projects: Quote, Validated Quote, Purchase Order,
                Invoice, etc.
              </div>
            </div>
            <div class="actions flex-row items-end">
              <CreateDocument class="mb-4" :resumable="resumable" @input="handleDocumentAdded" />
              <div class="">
                <FilterPicker :value="currentBrandFilter" @input="handleBrandFilterSelectChange" />
              </div>
            </div>
          </div>
          <ts-divider></ts-divider>
          <DocumentList
            label="Brand documents"
            :documents="brandDocuments"
            :can-upload="isProjectManager || isCustomer"
            @document-delete="handleDocumentDelete"
            @document-edit="handleDocumentEdit"
            @documents-download="handleDocumentsDownload"
            @file-added="handleFileAdded('brand', $event)"
            @file-success="handleFileSuccess"
            @file-error="handleFileError"
          />
        </div>
      </TabsContent>
    </div>
  </ProjectScaffold>
</template>

<style lang="scss" scoped>
.flex-row {
  flex-direction: column;
}
.items-end {
  align-items: flex-end;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.tabs {
  display: flex;

  .tabs-content {
    flex-grow: 1;
    padding: 40px 46px;
  }
}

.header {
  display: flex;

  .titles {
    flex-grow: 1;
  }

  .title {
    font-family: Roboto;
    font-size: 20px;
    color: #6d7587;
  }

  .subtitle {
    font-family: Roboto;
    font-size: 12px;
    color: #6d7587;

    margin-top: 12px;
  }

  .actions {
    flex-shrink: 0;
    margin-left: 50px;
    display: flex;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';
import { orderBy, sortBy } from 'lodash';
import moment from 'moment';

import { downloadProjectDocuments } from '@/api/workflow';

import ProjectScaffold from '../ProjectScaffold.vue';
import DocumentList from './DocumentList.vue';
import CreateDocument from './CreateDocument.vue';
import TabsNavigation from '@/components/TabsNavigation.vue';
import TabsContent from '@/components/TabsContent.vue';
import FilterPicker from '@/components/inputs/FilterPicker.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';

const removeExtension = value => {
  const reg = new RegExp(/(\.\w*$)/);
  return isNaN(value) ? value.replace(reg, '') : parseInt(value);
};

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    ProjectScaffold,
    DocumentList,
    CreateDocument,
    TabsNavigation,
    TabsContent,
    FilterPicker
  },
  data() {
    return {
      selectedTabId: null,
      uploadingDocuments: [],
      brandFilter: 'latest',
      creatorFilter: 'latest',
      filterOptions: {
        latest: { field: 'createdAt', order: 'asc', handler: asset => moment(asset.createdAt).unix() },
        oldest: { field: 'createdAt', order: 'desc', handler: asset => moment(asset.createdAt).unix() },
        asc: {
          field: 'name',
          order: 'asc',
          handler(asset) {
            const name = removeExtension(asset.name);
            return isNaN(name) ? name : parseInt(name);
          }
        },
        desc: {
          field: 'name',
          order: 'desc',
          handler(asset) {
            const name = removeExtension(asset.name);
            return isNaN(name) ? name.toLowerCase() : parseInt(name);
          }
        }
      }
    };
  },
  computed: {
    ...mapState('workflow', {
      currentProjectId: state => state.currentProjectId,
      currentProject: state => state.currentProject.value
    }),
    ...mapState('workflow/documents', {
      loadingDocuments: state => state.documents.loading,
      documents: state => state.documents.items,
      documentsError: state => state.documents.error
    }),
    currentCreatorFilter() {
      return this.creatorFilter;
    },
    currentBrandFilter() {
      return this.brandFilter;
    },
    tabs() {
      return [
        {
          id: 'creator',
          name: 'Creator',
          visible: this.isProjectManager || this.isCreator
        },
        {
          id: 'brand',
          name: 'Brand',
          visible: this.isProjectManager || this.isCustomer
        }
      ];
    },
    tabDefinitions() {
      return this.tabs.filter(tab => tab.visible);
    },
    creatorNames() {
      if (!this.currentProject) {
        return '';
      }
      return this.currentProject.users
        .filter(user => this.isUserCreator(user))
        .map(user => user.name)
        .join(', ');
    },
    creatorDocuments() {
      const documents = [
        ...this.documents.filter(document => document.group === 'creator'),
        ...this.uploadingDocuments.filter(document => document.group === 'creator')
      ];
      const { field, order, handler } = this.filterOptions[this.currentCreatorFilter];
      return orderBy(documents, [document => (handler ? handler(document) : document[field])], [order]);
    },
    brandDocuments() {
      const documents = [
        ...this.documents.filter(document => document.group === 'brand'),
        ...this.uploadingDocuments.filter(document => document.group === 'brand')
      ];

      const { field, order, handler } = this.filterOptions[this.currentBrandFilter];
      return orderBy(documents, [document => (handler ? handler(document) : document[field])], [order]);
    }
  },
  methods: {
    ...mapActions('workflow/documents', [
      'fetchCurrentProjectDocuments',
      'addCurrentProjectDocument',
      'updateCurrentProjectDocument',
      'removeCurrentProjectDocument'
    ]),
    handleCreatorFilterSelectChange(value) {
      this.creatorFilter = value;
    },
    handleBrandFilterSelectChange(value) {
      this.brandFilter = value;
    },
    async handleDocumentAdded(document) {
      if (document.type === 'uploaded-file') {
        this.uploadingDocuments = [
          ...this.uploadingDocuments,
          {
            ...document,
            id: document.resumableFile.uniqueIdentifier,
            group: this.selectedTabId,
            users: [],
            createdAt: new Date().toISOString(),
            createdBy: this.currentUser
          }
        ];
        this.resumable.upload();
      }
    },
    handleProjectDocumentsAdded(documents) {
      documents.forEach(document => {
        this.addCurrentProjectDocument({
          document: {
            ...document,
            fileId: document.file.id
          }
        });
      });
    },
    handleFileAdded(group, file) {
      this.uploadingDocuments = [
        ...this.uploadingDocuments,
        {
          id: file.uniqueIdentifier,
          name: file.fileName,
          group,
          users: [],
          resumableFile: file,
          createdAt: new Date().toISOString(),
          createdBy: this.currentUser,
          uploadError: false,
          uploadErrorMessage: ''
        }
      ];
    },
    async handleFileSuccess(file) {
      const document = this.uploadingDocuments.find(d => d.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (document) {
        try {
          await this.addCurrentProjectDocument({ document });
          this.uploadingDocuments = this.uploadingDocuments.filter(
            d => d.resumableFile.uniqueIdentifier !== file.uniqueIdentifier
          );
        } catch (err) {
          console.error('Server error when adding document', err);
          document.uploadError = true;
          document.uploadErrorMessage = err.message;
          this.showError('Error when adding document');
        }
      }
    },
    handleFileError(file, message) {
      const document = this.uploadingDocuments.find(d => d.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (document) {
        document.uploadError = true;
        document.uploadErrorMessage = message;
      }
    },
    handleDocumentEdit(document) {
      this.updateCurrentProjectDocument({ document });
    },
    handleDocumentDelete(document) {
      this.uploadingDocuments = this.uploadingDocuments.filter(
        d => d.resumableFile.uniqueIdentifier !== document.resumableFile.uniqueIdentifier
      );
      if (!document.resumableFile) {
        this.removeCurrentProjectDocument({ document });
      }
    },
    handleDocumentsDownload(documents) {
      this.downloadFile(
        downloadProjectDocuments({
          projectId: this.currentProjectId,
          assetIds: documents.map(d => d.id)
        }),
        `ts_documents_project${this.currentProjectId}.zip`
      );
    }
  },
  async mounted() {
    this.selectedTabId = this.tabDefinitions[0].id;

    this.fetchCurrentProjectDocuments();
    this.resumable.on('fileSuccess', file => {
      this.handleFileSuccess(file);
    });
    this.resumable.on('fileError', (file, message) => {
      this.handleFileError(file, message);
    });
  },
  watch: {
    documentsError(value) {
      if (value) {
        this.showError(value);
      }
    }
  }
};
</script>
